import React, { useState, useEffect } from 'react';
import {
  HStack,
  Text,
  VStack,
  Stack,
  Box,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Button,
} from '@chakra-ui/react';
import { RiSwordFill } from 'react-icons/ri';
import { RiMoonClearFill, RiMoonFill } from 'react-icons/ri';
import ConfettiExplosion from 'react-confetti-explosion';
import { useCharacter } from '../Contexts/CharacterInfo.js';
import theme from '../../../theme.js';
import LuckPointCheckbox from './LuckPointCheckbox.jsx';
import HealthProgressBox from './HealthBox/HealthProgressBox.jsx';
import { HealthAttributes } from '../Contexts/ConstantsCollection.js';

const VitalityContent = ({ luckPoints }) => {
  const {
    character,
    updateCurrentResilience,
    updateCurrentHealth,
    updateLuckPoints,
    clearUsedTraits,
    saveCharacter,
    loaded,
    updateWeaponBonus,
  } = useCharacter();
  const [showConfetti, setShowConfetti] = useState(false);
  const [damage, setDamage] = useState(0);

  const handleAddLuckPoint = () => {
    if (character.luckPoints.length < 3) {
      updateLuckPoints([...character.luckPoints, true]);
      saveCharacter();
    }

    if (loaded) {
      setShowConfetti(true);
      setTimeout(() => {
        setShowConfetti(false);
      }, 2000);
    }
  };

  const handleSubtractLuckPoint = val => {
    if (character.luckPoints.length > 0) {
      updateLuckPoints(
        character.luckPoints.slice(0, (character.luckPoints.length - val) * -1)
      );
      saveCharacter();
    }
  };

  const handleDamage = () => {
    if (damage > 0) {
      if (damage > character.currentResilience)
        updateCurrentHealth(
          Math.max(character.currentHealth - damage, character.deathThreshold)
        );
      else updateCurrentResilience(character.currentResilience - 1);
    }
    setDamage(0);
    saveCharacter();
  };

  const handleRest = isShortRest => {
    var newHealth = 0;
    if (isShortRest) {
      newHealth = Math.min(
        Math.round(character.totalHealth * 0.5) + character.currentHealth,
        character.totalHealth
      );
    } else {
      newHealth =
        character.currentHealth +
        Math.round(
          (character.totalHealth - Math.abs(character.currentHealth)) * 0.5
        );
    }
    updateCurrentHealth(newHealth);
    clearUsedTraits();
    if (character.primaryWeaponId)
      updateWeaponBonus(character.primaryWeaponId, 0);
    if (character.previousSave) saveCharacter();
  };

  useEffect(() => {
    handleAddLuckPoint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [luckPoints]);

  return (
    <Stack
      width="50%"
      align="center"
      justify="center"
      direction={{ base: 'column', md: 'column', lg: 'row' }}
      spacing={10}
    >
      <HealthProgressBox Attributes={HealthAttributes.resilience} />

      <VStack align="center" width="100%">
        <Box position="relative" top="-20px">
          {showConfetti && (
            <ConfettiExplosion
              colors={[theme.colors.luckPointMain]}
              force={0.3}
              duration={2000}
              particleCount={50}
              particleSize={7}
              width={500}
              left="-100px"
            />
          )}
        </Box>
        <Text
          fontSize="lg"
          fontWeight="semibold"
          left="-5px"
          position="relative"
        >
          Luck Points
        </Text>
        <HStack alignItems="center" justify="center">
          <LuckPointCheckbox
            checked={character.luckPoints[0] ?? false}
            value={0}
            onUnchecked={handleSubtractLuckPoint}
          />
          <LuckPointCheckbox
            checked={character.luckPoints[1] ?? false}
            value={1}
            onUnchecked={handleSubtractLuckPoint}
          />
          <LuckPointCheckbox
            checked={character.luckPoints[2] ?? false}
            value={2}
            onUnchecked={handleSubtractLuckPoint}
          />
        </HStack>
        <Stack
          width={'55%'}
          alignContent="center"
          align="center"
          alignItems="center"
          justify="center"
          direction={{ base: 'column', md: 'column', lg: 'row' }}
        >
          <NumberInput
            min={0}
            max={500}
            width="150px"
            defaultValue={0}
            value={damage}
            onChange={newDamage => setDamage(newDamage)}
          >
            <NumberInputField
              border="2px solid black"
              height={'40px'}
              minWidth={75}
            />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
          <Button minWidth={150} onClick={handleDamage}>
            <HStack margin={5} title="Take Damage">
              <Text>Take Damage</Text>
              <RiSwordFill size={24} />
            </HStack>
          </Button>
        </Stack>
        <Stack
          width={'75%'}
          alignContent="center"
          align="center"
          alignItems="center"
          justify="center"
          direction={{ base: 'column', md: 'column', lg: 'row' }}
        >
          <Button
            width={'40%'}
            minWidth={150}
            disabled={character.isDead}
            onClick={() => handleRest(false)}
          >
            <HStack margin={10} title="Short Rest">
              <Text>Short Rest</Text>
              <RiMoonFill size={22} />
            </HStack>
          </Button>
          <Button
            width={'60%'}
            minWidth={150}
            disabled={character.isDead}
            onClick={() => handleRest(true)}
          >
            <HStack margin={10} title="Long Rest">
              <Text>Long Rest</Text>
              <RiMoonClearFill size={25} />
            </HStack>
          </Button>
        </Stack>
      </VStack>
      <HealthProgressBox Attributes={HealthAttributes.health} />
    </Stack>
  );
};

export default VitalityContent;
